import { useEffect, useState } from 'react';
import { ResolvedRoleLevels } from '@blisspointmedia/bpm-types/dist/dashboarding';
import { useSetError } from '@src/redux/modals';
import { Mixpanel, MxE } from '../../../utils/mixpanelWrapper';
import { ChartHandle } from '../chart-handle';
import { decideSharingRequest, SharingOutcome, SharingTarget } from '../sharing-request-utils';
import { usePendingSharing } from './usePendingSharing';

interface UseSharingRequestArgs {
  company: string;
  /**
   * The note's target: a chart's handle, or `sharingTargetKey(dashboardId)`.
   *
   * Pass NULL for a draft chart. A draft has no row to write, and its request is read by the draft's
   * own save path and its footer instead — so this hook must neither act on it nor clear it. Null is
   * how it stays out of the way, the same way `useChartEditGate` takes a null chart id for a draft.
   */
  noteTarget: ChartHandle | string | null;
  /** What the frontend knows about the record — see `SharingTargetState`. */
  target: SharingTarget;
  userRole: number;
  canEdit: boolean;
  /** The page this control sits on, for Mixpanel. A `PAGE_NAMES` value. */
  page: string;
  /**
   * Persist the pair. MUST REJECT if the save failed, and must report the failure itself — this hook
   * does not report a rejection, because the handler already has. Both surfaces' existing
   * `handlePermissionChange` functions satisfy that contract as written.
   */
  onSave: (levels: ResolvedRoleLevels) => Promise<void>;
  /**
   * Hold the request without consuming it. Set while another writer owns this same row — a pending
   * or in-flight chat promotion — because both would `saveChart` the same id in one commit, one with
   * the SAVED config and one with the working copy, and the loser silently reverts the other.
   *
   * Deferring is also the correct order, not just the safe one: an Update seeds the envelope cache
   * with server truth, so by the time this runs the record's config IS the saved edit and the
   * audience lands on top of it.
   */
  defer?: boolean;
}

/**
 * Run one chat-driven sharing request against the record on this page.
 *
 * Deliberately thin. Every rule lives in `decideSharingRequest`, which is pure and covered by a table
 * of cases; this hook reads the note, performs the effect the decision names, and clears the note
 * unless the decision says to hold it.
 *
 * Returns `openWith` for the confirm case, which the caller hands to `PermissionDropdown` so the user
 * confirms on the control they already know rather than a bespoke dialog.
 */
export const useSharingRequest = ({
  company,
  noteTarget,
  target,
  userRole,
  canEdit,
  page,
  onSave,
  defer = false
}: UseSharingRequestArgs): {
  openWith: ResolvedRoleLevels | undefined;
  closeConfirm: () => void;
} => {
  const setError = useSetError();
  const { pendingSharing, clearPendingSharing } = usePendingSharing(company, noteTarget);
  const [openWith, setOpenWith] = useState<ResolvedRoleLevels | undefined>(undefined);

  useEffect(() => {
    // Held, not consumed — see `defer`'s own doc comment. The note stays exactly as it is until the
    // other writer's save resolves and this hook runs again with `defer: false`.
    if (defer) return;
    if (!pendingSharing) return;
    const decision = decideSharingRequest({
      request: pendingSharing,
      target,
      userRole,
      canEdit
    });

    // The record is still loading. Leave the note and decide again when it arrives.
    if (decision.outcome === SharingOutcome.HOLD) return;

    clearPendingSharing();

    if (decision.outcome === SharingOutcome.REFUSE) {
      setError({ message: decision.message });
      return;
    }
    // Asked for what the record already has. Say nothing and write nothing.
    if (decision.outcome === SharingOutcome.NO_CHANGE) return;

    if (decision.outcome === SharingOutcome.CONFIRM) {
      setOpenWith(decision.levels);
      // The only path in this feature that can WIDEN who sees a record without a save having
      // happened yet — countable separately from `PermissionDropdown`'s own `RecordSaved`, which
      // still fires if and when the user actually confirms.
      Mixpanel.track(MxE.ActionTriggered, { page, action: 'chat_sharing_confirm_opened' });
      return;
    }

    onSave(decision.levels)
      .then(() => {
        // Parity with the dialog, which fires this inside its own handleSave — a function the chat
        // path never reaches, so without this a chat-driven change is invisible in Mixpanel.
        Mixpanel.track(MxE.RecordSaved, { page, entity: 'permissions' });
        // And separately countable from a dialog-driven change. `ActionTriggered` rather than a new
        // field on `RecordSaved`, whose prop shape is shared with every other caller of that event.
        Mixpanel.track(MxE.ActionTriggered, { page, action: 'chat_sharing_change' });
      })
      // `onSave` has already reported the failure; the rejection is its contract, not an unhandled
      // error. Swallow it so the user is not told twice.
      .catch(() => {});
    // Reads the current closures on a fresh note or a settled record, the same way the chat-driven
    // promotion effect does. Adding the unmemoized handlers would only churn the list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingSharing, target, userRole, canEdit, defer]);

  return { openWith, closeConfirm: () => setOpenWith(undefined) };
};
